/**
 * Publisher Service API
 *
 * Service layer for Publisher Service operations
 * Handles publishers, books, attachments, and bulk uploads
 */

import publisherClient from '../clients/publisher.client';
import {
  PublisherResponse,
  PublisherListResponse,
  PublisherListParams,
  CreatePublisherRequest,
  UpdatePublisherRequest,
  BookResponse,
  BookListResponse,
  BookListParams,
  UpdateBookRequest,
  UpdateBookStatusRequest,
  BookLifecycleListResponse,
  BookAttachmentResponse,
  BookAttachmentListResponse,
  BookAttachmentListParams,
  AttachmentUploadFormData,
  PublisherRequestResponse,
  PublisherRequestListResponse,
  PublisherRequestListParams,
  BulkUploadFormData,
  ContractRequest,
  ContractDocumentType,
  PublisherDocumentResponse,
  SubmitContractRequest,
} from '../types/publisher.types';
import { API_ENDPOINTS } from '../config';

// ===========================
// PUBLISHER ENDPOINTS
// ===========================

export const publisherService = {
  /**
   * Create a new publisher profile
   */
  async createPublisher(data: CreatePublisherRequest): Promise<PublisherResponse> {
    const response = await publisherClient.post('/publishers', data);
    return response.data;
  },

  /**
   * List all publishers with pagination and filtering
   */
  async listPublishers(params?: PublisherListParams): Promise<PublisherListResponse> {
    const response = await publisherClient.get('/publishers', { params });
    return response.data;
  },

  /**
   * Get publisher details by ID
   */
  async getPublisher(publisherId: string): Promise<PublisherResponse> {
    const response = await publisherClient.get(`/publishers/${publisherId}`);
    return response.data;
  },

  /**
   * Update publisher information
   */
  async updatePublisher(publisherId: string, data: UpdatePublisherRequest): Promise<PublisherResponse> {
    const response = await publisherClient.put(`/publishers/${publisherId}`, data);
    return response.data;
  },

  /**
   * Delete publisher profile (soft delete)
   */
  async deletePublisher(publisherId: string): Promise<void> {
    await publisherClient.delete(`/publishers/${publisherId}`);
  },

  /**
   * Get publisher by user ID
   */
  async getPublisherByUserId(userId: string): Promise<PublisherResponse> {
    const response = await publisherClient.get(`/publishers/user/${userId}`);
    return response.data;
  },
};

// ===========================
// BOOK ENDPOINTS
// ===========================

export const bookService = {
  /**
   * List all books with pagination and filtering (for ALC backoffice)
   */
  async listAllBooks(params?: BookListParams): Promise<BookListResponse> {
    const response = await publisherClient.get('/books', { params });
    return response.data;
  },

  /**
   * List publisher's books with pagination and filtering
   */
  async listPublisherBooks(publisherId: string, params?: BookListParams): Promise<BookListResponse> {
    const response = await publisherClient.get(`/publishers/${publisherId}/books`, { params });
    return response.data;
  },

  /**
   * Get book details by ID
   */
  async getBook(bookId: string): Promise<BookResponse> {
    const response = await publisherClient.get(`/books/${bookId}`);
    return response.data;
  },

  /**
   * Update book information (DRAFT or REJECTED status only)
   */
  async updateBook(bookId: string, data: UpdateBookRequest): Promise<BookResponse> {
    const response = await publisherClient.put(`/books/${bookId}`, data);
    return response.data;
  },

  /**
   * Update book status (admin/backoffice operation)
   */
  async updateBookStatus(bookId: string, data: UpdateBookStatusRequest): Promise<BookResponse> {
    const response = await publisherClient.patch(`/books/${bookId}/status`, data);
    return response.data;
  },

  /**
   * Get book lifecycle history
   */
  async getBookLifecycle(bookId: string): Promise<BookLifecycleListResponse> {
    const response = await publisherClient.get(`/books/${bookId}/lifecycle`);
    return response.data;
  },
};

// ===========================
// BOOK ATTACHMENT ENDPOINTS
// ===========================

export const attachmentService = {
  /**
   * Upload book attachment (cover, content, etc.)
   */
  async uploadBookAttachment(
    bookId: string,
    { attachmentType, file }: AttachmentUploadFormData
  ): Promise<BookAttachmentResponse> {
    const formData = new FormData();
    formData.append('attachmentType', attachmentType);
    formData.append('file', file);

    const response = await publisherClient.post(`/books/${bookId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List book attachments with filtering
   */
  async listBookAttachments(
    bookId: string,
    params?: BookAttachmentListParams
  ): Promise<BookAttachmentListResponse> {
    const response = await publisherClient.get(`/books/${bookId}/attachments`, { params });
    return response.data;
  },

  /**
   * Get attachment details by ID
   */
  async getAttachment(attachmentId: string): Promise<BookAttachmentResponse> {
    const response = await publisherClient.get(`/attachments/${attachmentId}`);
    return response.data;
  },

  /**
   * Delete attachment (soft delete)
   */
  async deleteAttachment(attachmentId: string): Promise<void> {
    await publisherClient.delete(`/attachments/${attachmentId}`);
  },

  /**
   * Download attachment file
   * Returns the file as a Blob for download
   */
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    const response = await publisherClient.get(`/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download attachment file with custom filename
   */
  async downloadAttachmentWithFilename(
    attachmentId: string,
    filename?: string
  ): Promise<void> {
    const blob = await this.downloadAttachment(attachmentId);

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `attachment-${attachmentId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

// ===========================
// PUBLISHER REQUEST ENDPOINTS (Bulk Upload)
// ===========================

export const bulkUploadService = {
  /**
   * Submit bulk book upload request (Excel file)
   */
  async submitBulkUploadRequest(
    publisherId: string,
    { file }: BulkUploadFormData
  ): Promise<PublisherRequestResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await publisherClient.post(`/publishers/${publisherId}/requests`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List publisher's upload requests with pagination and filtering
   */
  async listPublisherRequests(
    publisherId: string,
    params?: PublisherRequestListParams
  ): Promise<PublisherRequestListResponse> {
    const response = await publisherClient.get(`/publishers/${publisherId}/requests`, { params });
    return response.data;
  },

  /**
   * Get request details by ID
   */
  async getRequest(requestId: string): Promise<PublisherRequestResponse> {
    const response = await publisherClient.get(`/requests/${requestId}`);
    return response.data;
  },

  /**
   * Get books from a specific upload request
   */
  async getRequestBooks(
    requestId: string,
    params?: { page?: number; pageSize?: number }
  ): Promise<BookListResponse> {
    const response = await publisherClient.get(`/requests/${requestId}/books`, { params });
    return response.data;
  },
};

// ===========================
// CONTRACT REQUEST ENDPOINTS
// ===========================

export const contractService = {
  /**
   * List contract requests for a publisher
   * GET /publishers/{publisherId}/contract-requests
   */
  async getContractRequest(publisherId: string): Promise<ContractRequest | null> {
    const response = await publisherClient.get(
      API_ENDPOINTS.PUBLISHER.CONTRACT_REQUESTS(publisherId)
    );
    // API returns { data: ContractRequest[], pagination: ... }
    const list: ContractRequest[] = response.data.data ?? [];
    return list.length > 0 ? list[0] : null;
  },

  /**
   * Get contract request details by ID
   * GET /contract-requests/{contractRequestId}
   */
  async getContractRequestById(contractRequestId: string): Promise<ContractRequest> {
    const response = await publisherClient.get(
      API_ENDPOINTS.PUBLISHER.CONTRACT_REQUEST_BY_ID(contractRequestId)
    );
    return response.data;
  },

  /**
   * List publisher's verification documents
   * GET /publishers/{publisherId}/documents
   */
  async listPublisherDocuments(publisherId: string): Promise<PublisherDocumentResponse[]> {
    const response = await publisherClient.get(
      API_ENDPOINTS.PUBLISHER.PUBLISHER_DOCUMENTS(publisherId)
    );
    return response.data.data;
  },

  /**
   * Upload a publisher verification document
   * POST /publishers/{publisherId}/documents/{documentType}
   */
  async uploadPublisherDocument(
    publisherId: string,
    documentType: ContractDocumentType,
    file: File
  ): Promise<PublisherDocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await publisherClient.post(
      API_ENDPOINTS.PUBLISHER.PUBLISHER_DOCUMENT_BY_TYPE(publisherId, documentType),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Submit a contract request for review
   * POST /publishers/{publisherId}/contract-requests
   */
  async submitContractRequest(publisherId: string, body?: SubmitContractRequest): Promise<ContractRequest> {
    const response = await publisherClient.post(
      API_ENDPOINTS.PUBLISHER.CONTRACT_REQUESTS(publisherId),
      body
    );
    return response.data;
  },

  /**
   * Download a document file
   * GET /documents/{documentId}/download
   */
  async downloadDocument(documentId: string): Promise<Blob> {
    const response = await publisherClient.get(
      API_ENDPOINTS.PUBLISHER.DOCUMENT_DOWNLOAD(documentId),
      { responseType: 'blob' }
    );
    return response.data;
  },
};

// ===========================
// COMBINED API OBJECT
// ===========================

/**
 * Complete Publisher API service
 * Combines all service modules for easy import
 */
export const publisherApi = {
  publishers: publisherService,
  books: bookService,
  attachments: attachmentService,
  bulkUpload: bulkUploadService,
  contract: contractService,
};

export default publisherApi;

// ===========================
// HELPER FUNCTIONS
// ===========================

/**
 * Helper to build query parameters for book search
 */
export function buildBookSearchParams(
  search?: string,
  status?: string,
  category?: string,
  page?: number,
  pageSize?: number
): BookListParams {
  const params: BookListParams = {};

  if (search) params.search = search;
  if (status) params.status = status as any;
  if (category) params.category = category;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;

  return params;
}

/**
 * Helper to format file size in human readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Helper to validate file upload requirements
 */
export function validateFileUpload(
  file: File,
  allowedTypes: string[],
  maxSizeMB: number
): { valid: boolean; error?: string } {
  // Check file type
  if (!allowedTypes.some(type => file.type.includes(type))) {
    return {
      valid: false,
      error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`
    };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds limit. Maximum size: ${maxSizeMB}MB`
    };
  }

  return { valid: true };
}